import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, Building2, Phone, Users as UsersIcon, ShieldAlert } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { useAuth } from "@/lib/auth";

const TABS = [
  { key: "agents", labelKey: "reports.tab_agents", Icon: Building2 },
  { key: "users",  labelKey: "reports.tab_users",  Icon: UsersIcon },
];

export default function Reports() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "super_admin" || me?.role === "tenant_admin";
  const [tab, setTab] = useState("agents");

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-[900px] mx-auto fade-in" data-testid="reports-forbidden">
        <div className="card text-center py-16">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[var(--sand-100)] text-[var(--ink-500)] grid place-items-center mb-4">
            <ShieldAlert size={22} />
          </div>
          <h1 className="font-display text-[22px] text-[var(--ink-900)]">{t("reports.title")}</h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-2">{t("reports.admin_only")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-[1180px] mx-auto fade-in w-full" data-testid="reports-screen">
      <div className="mb-6">
        <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
          {t("nav.reports")}
        </div>
        <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">{t("reports.title")}</h1>
        <p className="text-[14px] text-[var(--ink-500)] mt-1">{t("reports.sub")}</p>
      </div>

      <div className="flex items-center gap-2 mb-5" data-testid="reports-tabs">
        {TABS.map(({ key, labelKey, Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`reports-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 px-4 h-9 rounded-full text-[13px] font-medium transition-colors border ${
                active
                  ? "bg-[var(--teal-600)] text-white border-[var(--teal-600)] shadow-sm"
                  : "bg-white text-[var(--ink-700)] border-[var(--border-soft)] hover:bg-[var(--sand-50)]"
              }`}
            >
              <Icon size={15} strokeWidth={1.75} />
              {t(labelKey)}
            </button>
          );
        })}
      </div>

      {tab === "agents" ? <AgentsOnlineReport /> : <UserActivityReport />}
    </div>
  );
}

/* ───────────────────────── Agents online ───────────────────────── */
function AgentsOnlineReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    http
      .get("/api/reports/agents-online")
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Kon rapport niet laden"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.agents;
    return data.agents.filter((a) =>
      (a.agent_name || "").toLowerCase().includes(q) ||
      (a.agent_company || "").toLowerCase().includes(q) ||
      (a.agent_phone || "").toLowerCase().includes(q),
    );
  }, [data, query]);

  if (loading) return <div className="card py-10 text-center text-[var(--ink-500)]">Laden…</div>;
  if (error) return <div className="card py-6 text-[var(--danger)]" data-testid="reports-agents-error">{error}</div>;
  if (!data) return null;

  return (
    <div data-testid="reports-agents-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <p className="text-[13px] text-[var(--ink-500)] max-w-2xl">{t("reports.agents_sub")}</p>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek makelaar/kantoor…"
            data-testid="reports-agents-search"
            className="h-9 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--teal-300)] w-[240px]"
          />
          <div
            className="text-[12px] text-[var(--ink-500)]"
            data-testid="reports-agents-total"
          >
            {t("reports.total_online")}: <span className="font-semibold text-[var(--ink-900)]">{data.total_online}</span>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <table className="w-full text-[13px]" data-testid="reports-agents-table">
          <thead className="bg-[var(--sand-50)] text-[var(--ink-500)] text-[12px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3 font-medium">{t("reports.col_agent")}</th>
              <th className="text-left px-4 py-3 font-medium">{t("reports.col_company")}</th>
              <th className="text-left px-4 py-3 font-medium">{t("reports.col_phone")}</th>
              <th className="text-left px-4 py-3 font-medium">{t("reports.col_regions")}</th>
              <th className="text-right px-4 py-3 font-medium">{t("reports.col_count")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-[var(--ink-500)]">
                  {t("reports.no_data")}
                </td>
              </tr>
            ) : (
              filtered.map((a, i) => (
                <tr
                  key={`${a.agent_name || "_"}-${a.agent_company || "_"}-${i}`}
                  className="border-t border-[var(--border-soft)] hover:bg-[var(--sand-50)]/60"
                  data-testid={`reports-agent-row-${i}`}
                >
                  <td className="px-4 py-3 text-[var(--ink-900)] font-medium">
                    {a.agent_name || <span className="text-[var(--ink-400)] italic">{t("reports.unknown_agent")}</span>}
                  </td>
                  <td className="px-4 py-3 text-[var(--ink-700)]">{a.agent_company || "—"}</td>
                  <td className="px-4 py-3 text-[var(--ink-700)]">
                    {a.agent_phone ? (
                      <span className="inline-flex items-center gap-1">
                        <Phone size={12} strokeWidth={1.75} className="text-[var(--ink-400)]" />
                        {a.agent_phone}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(a.postal_codes || []).slice(0, 6).map((pc) => (
                        <span
                          key={pc.postal_code}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--sand-50)] border border-[var(--border-soft)] text-[11px] text-[var(--ink-700)]"
                        >
                          {pc.postal_code}
                          <span className="text-[var(--ink-400)]">·{pc.count}</span>
                        </span>
                      ))}
                      {(a.postal_codes || []).length > 6 && (
                        <span className="text-[11px] text-[var(--ink-400)] self-center">+{a.postal_codes.length - 6}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-[var(--ink-900)]">
                    {a.count}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ───────────────────────── User activity ───────────────────────── */
const PERIODS = [
  { days: 7,   labelKey: "reports.period_7" },
  { days: 30,  labelKey: "reports.period_30" },
  { days: 90,  labelKey: "reports.period_90" },
  { days: 365, labelKey: "reports.period_365" },
];

const TYPE_LABELS = {
  calls: "Calls",
  brieven: "Brieven",
  status_wijzigingen: "Status",
  notities: "Notities",
  eigenaar_updates: "Eigenaar",
  prospectie_contacten: "Prospectie",
  gearchiveerd: "Op rust",
  hersteld: "Hersteld",
  toegewezen: "Toegewezen",
  vrijgegeven: "Vrijgegeven",
  shortlist: "Shortlist",
};

function LastLoginCell({ iso }) {
  if (!iso) {
    return <span className="text-[var(--ink-400)] italic text-[11px]">nooit</span>;
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return <span className="text-[var(--ink-400)]">—</span>;
  const minutes = Math.round((Date.now() - d.getTime()) / 60000);
  let rel;
  if (minutes < 1) rel = "zojuist";
  else if (minutes < 60) rel = `${minutes} min geleden`;
  else if (minutes < 60 * 24) rel = `${Math.floor(minutes / 60)} u geleden`;
  else if (minutes < 60 * 24 * 7) rel = `${Math.floor(minutes / (60 * 24))} d geleden`;
  else rel = d.toLocaleDateString("nl-BE", { day: "2-digit", month: "short", year: "numeric" });
  const fullDate = d.toLocaleString("nl-BE", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  return (
    <span title={fullDate} className="text-[12px] text-[var(--ink-700)]">{rel}</span>
  );
}

function UserActivityReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    http
      .get(`/api/reports/user-activity?days=${days}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Kon rapport niet laden"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const categories = data?.categories || [];

  return (
    <div data-testid="reports-users-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <p className="text-[13px] text-[var(--ink-500)] max-w-2xl">{t("reports.users_sub")}</p>
        <div className="flex items-center gap-2" data-testid="reports-users-period">
          <span className="text-[12px] text-[var(--ink-500)]">{t("reports.period_label")}:</span>
          {PERIODS.map((p) => {
            const active = days === p.days;
            return (
              <button
                key={p.days}
                type="button"
                data-testid={`reports-period-${p.days}`}
                onClick={() => setDays(p.days)}
                className={`h-8 px-3 rounded-full text-[12px] font-medium border transition-colors ${
                  active
                    ? "bg-[var(--teal-600)] text-white border-[var(--teal-600)]"
                    : "bg-white text-[var(--ink-700)] border-[var(--border-soft)] hover:bg-[var(--sand-50)]"
                }`}
              >
                {t(p.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="card py-10 text-center text-[var(--ink-500)]">Laden…</div>
      ) : error ? (
        <div className="card py-6 text-[var(--danger)]" data-testid="reports-users-error">{error}</div>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-[13px]" data-testid="reports-users-table">
            <thead className="bg-[var(--sand-50)] text-[var(--ink-500)] text-[12px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium sticky left-0 bg-[var(--sand-50)] z-10">{t("reports.col_user")}</th>
                <th className="text-left px-4 py-3 font-medium">{t("reports.col_role")}</th>
                <th className="text-left px-4 py-3 font-medium whitespace-nowrap">{t("reports.col_last_login")}</th>
                {categories.map((c) => (
                  <th key={c} className="text-right px-3 py-3 font-medium whitespace-nowrap">
                    {TYPE_LABELS[c] || c}
                  </th>
                ))}
                <th className="text-right px-4 py-3 font-medium">{t("reports.col_total")}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.users || []).length === 0 ? (
                <tr>
                  <td colSpan={categories.length + 4} className="px-4 py-10 text-center text-[var(--ink-500)]">
                    {t("reports.no_data")}
                  </td>
                </tr>
              ) : (
                data.users.map((u, i) => (
                  <tr
                    key={u.user_id}
                    className="border-t border-[var(--border-soft)] hover:bg-[var(--sand-50)]/60"
                    data-testid={`reports-user-row-${i}`}
                  >
                    <td className="px-4 py-3 sticky left-0 bg-white">
                      <div className="text-[var(--ink-900)] font-medium">{u.user_name}</div>
                      {u.user_email && (
                        <div className="text-[11px] text-[var(--ink-500)]">{u.user_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[var(--ink-500)]">
                      {u.role ? t(`roles.${u.role}`) : "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`reports-user-last-login-${i}`}>
                      <LastLoginCell iso={u.last_login_at} />
                    </td>
                    {categories.map((c) => (
                      <td key={c} className="px-3 py-3 text-right tabular-nums text-[var(--ink-700)]">
                        {u.by_type?.[c] ? u.by_type[c] : <span className="text-[var(--ink-300)]">0</span>}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-[var(--ink-900)]">
                      {u.total}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
